import PropTypes from "prop-types";
import React from "react";
import Pagination from "react-bootstrap/Pagination";
import DropdownFilter from "../components/DropdownFilter";
import LoadingOverlay from "../components/LoadingOverlay";
import PageResultsCount from "../components/PageResultsCount";
import { calculatePaginationRange, pluralize } from "../utils";

const defaultPageSizeSelection = [
  { label: "20", value: "20" },
  { label: "100", value: "100" },
  { label: "200", value: "200" },
  { label: "500", value: "500" },
  { label: "1000", value: "1000" }
];

export const PaginatePropTypes = {
  pagination: PropTypes.shape(Pagination.propTypes),
  defaultPage: PropTypes.number,
  page: PropTypes.number,
  pageId: PropTypes.string,
  pageSize: PropTypes.number,
  totalResults: PropTypes.number,
  numPages: PropTypes.number,
  showPageNumbers: PropTypes.bool,
  onPageChange: PropTypes.func,
  showLoading: PropTypes.bool,
  showLoadingOverlay: PropTypes.bool,
  enablePageSizeSelector: PropTypes.bool,
  pageSizeSelectorItems: PropTypes.arrayOf(PropTypes.shape({})),
  enablePageSizeLimit: PropTypes.bool,
  hidePaging: PropTypes.bool,
  addTopMargin: PropTypes.bool,
  showTotalResults: PropTypes.bool,
  defaultPageId: PropTypes.string,
  nextPageId: PropTypes.string,
  prevPageId: PropTypes.string,
  usePageIds: PropTypes.bool
};

const DEFAULT_PAGINATION = {
  prev: true,
  next: true,
  first: true,
  last: false,
  ellipsis: false,
  boundaryLinks: false,
  maxButtons: 7
};

const withPagination = WrappedComponent => {
  function Paginate(props) {
    const {
      page: activePage = 1,
      pageId: activePageId = "",
      pagination = {},
      pageSizeSelectorItems,
      hidePaging = false,
      prevPageId = null,
      usePageIds = false,
      defaultPage = 1,
      onPageChange = () => undefined,
      pageSize: activePageSize = 20,
      nextPageId = "",
      numPages = 1,
      showPageNumbers = false,
      totalResults = 0,
      showTotalResults = true,
      showLoading = false,
      enablePageSizeSelector = false,
      enablePageSizeLimit,
      addTopMargin = false,
      showLoadingOverlay = false
    } = props;

    const handlePageChange = usePageIds
      ? (newPageId, newPageSize) => {
          const { pageId, pageSize } = props;
          if (newPageId === pageId && newPageSize === pageSize) return;
          onPageChange(newPageId ?? pageId, newPageSize || pageSize);
        }
      : (newPage, newPageSize) => {
          const { page, pageSize } = props;
          if (newPage === page && newPageSize === pageSize) return;
          onPageChange(newPage || page, newPageSize || pageSize);
        };

    const paginationProps = {
      ...DEFAULT_PAGINATION,
      ...pagination,
      activePage,
      items: numPages,
      onSelect: page => handlePageChange(page, activePageSize)
    };

    const PagesizeDropdown = () => {
      if (!enablePageSizeSelector) return null;
      const defaultValue = activePageSize.toString();
      let menuItems = pageSizeSelectorItems || defaultPageSizeSelection;
      if (enablePageSizeLimit && totalResults) {
        const cutOff = menuItems.findIndex(
          ({ value }) => value >= totalResults
        );
        if (cutOff !== -1) {
          menuItems = menuItems.slice(0, cutOff + 1);
        }
      }
      return (
        <DropdownFilter
          dropup
          chevronIcon
          disabled={showLoading}
          key="page-size-dropdown"
          eventKey={defaultValue}
          title={defaultValue}
          field="pageSize"
          value={defaultValue}
          containerClassName="page-size-dropdown"
          styles={{
            button: {
              minWidth: "auto",
              textAlign: "right"
            }
          }}
          menuAlign="end"
          menuItems={menuItems}
          onFilterChange={({ value }) =>
            usePageIds
              ? handlePageChange("", parseInt(value, 10))
              : handlePageChange(defaultPage, parseInt(value, 10))
          }
        />
      );
    };

    const PagingComponent = () => {
      const { maxButtons } = paginationProps;
      const paginationRange = usePageIds
        ? []
        : calculatePaginationRange(activePage, numPages, maxButtons);
      const items = paginationRange.map((pageNumber, pageIdx) => (
        <Pagination.Item
          active={pageNumber === activePage}
          onClick={
            pageNumber !== activePage
              ? () => handlePageChange(pageNumber)
              : null
          }
          key={pageIdx}
        >
          {pageNumber}
        </Pagination.Item>
      ));

      return (
        <div
          className={`pagination-toolbar ${addTopMargin ? "margin-top" : ""}`}
        >
          <Pagination>
            {paginationProps.first && (
              <Pagination.First
                className="page-item--first"
                disabled={
                  usePageIds
                    ? activePageId === ""
                    : !numPages || activePage <= 1
                }
                onClick={() =>
                  usePageIds ? handlePageChange("") : handlePageChange(1)
                }
              />
            )}
            {paginationProps.prev && (
              <Pagination.Prev
                className="page-item--previous"
                disabled={
                  usePageIds
                    ? activePageId === ""
                    : !numPages || activePage <= 1
                }
                onClick={() =>
                  usePageIds
                    ? handlePageChange(prevPageId)
                    : handlePageChange(activePage - 1)
                }
              />
            )}
            {showPageNumbers && items}
            {paginationProps.next && (
              <Pagination.Next
                className="page-item--next"
                disabled={
                  usePageIds
                    ? !nextPageId
                    : !numPages || activePage === numPages
                }
                onClick={() =>
                  usePageIds
                    ? handlePageChange(nextPageId)
                    : handlePageChange(activePage + 1)
                }
              />
            )}
            {paginationProps.last && (
              <Pagination.Last
                className="page-item--last"
                disabled={!numPages || activePage === numPages}
                onClick={() => handlePageChange(numPages)}
              />
            )}
            {showTotalResults && (
              <li className="pagination__results-count">
                {showLoading ? (
                  <>Loading..</>
                ) : (
                  <PageResultsCount
                    count={totalResults}
                    postfix={pluralize(" Result", totalResults)}
                  />
                )}
              </li>
            )}
          </Pagination>
          <PagesizeDropdown />
        </div>
      );
    };

    return (
      <div className="samples-grid__container">
        {showLoadingOverlay && <LoadingOverlay />}
        <WrappedComponent {...props} />
        {!hidePaging && (
          <div className="rlbs">
            <PagingComponent />
          </div>
        )}
      </div>
    );
  }

  Paginate.propTypes = {
    ...(WrappedComponent.propTypes || {}),
    ...PaginatePropTypes
  };

  return Paginate;
};

export default withPagination;
